import type { Trip } from './trips';
import type { Booking } from './bookings';

export type TripCostStyle = 'budget' | 'comfort' | 'premium';

export type TripCostConfidence = {
  rangeMin: number;
  rangeMax: number;
  currency: string;
  drivers: { transport: number; stay: number; inDestination: number };
  breakdown: Array<{ category: string; min: number; max: number }>;
  confidence: 'high' | 'medium' | 'low';
  reasons: string[];
  nights: number;
  partySize: number;
  style: TripCostStyle;
  bufferPct: number;
};

export type TripCostConfidenceInput = {
  trip: Trip;
  bookings?: Booking[];
  style?: TripCostStyle;
  partySize?: number;
  bufferPct?: number;
};

const PER_DIEM_USD: Record<TripCostStyle, { min: number; max: number }> = {
  budget: { min: 55, max: 90 },
  comfort: { min: 95, max: 165 },
  premium: { min: 180, max: 320 },
};

const NIGHTLY_USD: Record<TripCostStyle, { min: number; max: number }> = {
  budget: { min: 70, max: 120 },
  comfort: { min: 130, max: 220 },
  premium: { min: 250, max: 450 },
};

const countNights = (start: Date, end: Date): number => {
  const ms = end.getTime() - start.getTime();
  if (!Number.isFinite(ms) || ms <= 0) return 1;
  return Math.max(1, Math.ceil(ms / (24 * 60 * 60 * 1000)));
};

const sumBookingTotals = (bookings: Booking[], types: Booking['type'][]): number => {
  return bookings
    .filter((b) => types.includes(b.type))
    .reduce((sum, b) => {
      const amount = typeof b.amount === 'number' ? b.amount : Number(b.amount);
      return Number.isFinite(amount) ? sum + amount : sum;
    }, 0);
};

const INTER_CITY_FLIGHT_USD: Record<TripCostStyle, { min: number; max: number }> = {
  budget: { min: 90, max: 220 },
  comfort: { min: 180, max: 420 },
  premium: { min: 350, max: 850 },
};

const LOCAL_TRANSPORT_PER_DAY_USD: Record<TripCostStyle, { min: number; max: number }> = {
  budget: { min: 8, max: 18 },
  comfort: { min: 15, max: 35 },
  premium: { min: 30, max: 70 },
};

const countCities = (trip: Trip): number => {
  if (trip.destinations && trip.destinations.length > 0) {
    return trip.destinations.length;
  }
  return trip.destination ? 1 : 1;
};

export function computeTripCostConfidence(input: TripCostConfidenceInput): TripCostConfidence {
  const { trip, bookings = [] } = input;
  const style = input.style ?? 'comfort';
  const partySize = Math.max(1, input.partySize ?? 1);
  const bufferPct = input.bufferPct ?? 12;
  const currency = (trip.baseCurrency || 'USD').toUpperCase();
  const nights = countNights(trip.startDate, trip.endDate);
  const days = Math.max(1, nights);
  const cityCount = countCities(trip);
  const interCitySegments = Math.max(0, cityCount - 1);

  const knownTransport = sumBookingTotals(bookings, ['flight', 'train', 'ferry', 'car']);
  const knownStay = sumBookingTotals(bookings, ['hotel']);
  const hasFlightBookings = bookings.some((b) => b.type === 'flight' && b.amount != null);

  let transportMin: number;
  let transportMax: number;
  if (knownTransport > 0) {
    transportMin = knownTransport * 0.95;
    transportMax = knownTransport * 1.08;
  } else {
    const interCityMin =
      interCitySegments * partySize * INTER_CITY_FLIGHT_USD[style].min;
    const interCityMax =
      interCitySegments * partySize * INTER_CITY_FLIGHT_USD[style].max;
    const localMin = days * partySize * LOCAL_TRANSPORT_PER_DAY_USD[style].min;
    const localMax = days * partySize * LOCAL_TRANSPORT_PER_DAY_USD[style].max;
    transportMin = interCityMin + localMin;
    transportMax = interCityMax + localMax;
  }

  const stayMin =
    knownStay > 0 ? knownStay * 0.95 : nights * partySize * 0.5 * NIGHTLY_USD[style].min;
  const stayMax =
    knownStay > 0 ? knownStay * 1.1 : nights * partySize * NIGHTLY_USD[style].max;

  const inDestMin = days * partySize * PER_DIEM_USD[style].min;
  const inDestMax = days * partySize * PER_DIEM_USD[style].max;

  const subtotalMin = transportMin + stayMin + inDestMin;
  const subtotalMax = transportMax + stayMax + inDestMax;
  const bufferFactor = 1 + bufferPct / 100;

  const rangeMin = Math.round(subtotalMin * bufferFactor);
  const rangeMax = Math.round(subtotalMax * bufferFactor);

  const knownCount = bookings.filter((b) => b.amount != null).length;
  let confidence: TripCostConfidence['confidence'] = 'low';
  if (knownTransport > 0 && knownStay > 0) confidence = 'high';
  else if (knownCount > 0 || trip.budget) confidence = 'medium';

  const reasons: string[] = [];
  if (knownTransport > 0) {
    reasons.push('Includes synced transport bookings (flights, trains, etc.)');
  } else if (interCitySegments > 0) {
    reasons.push(
      `Estimates ${interCitySegments} inter-city flight${interCitySegments === 1 ? '' : 's'} plus local transit`,
    );
  } else {
    reasons.push('Estimates local transit only — add flight bookings for tighter numbers');
  }
  if (knownStay > 0) reasons.push('Includes synced lodging bookings');
  if (knownCount === 0) reasons.push(`Based on ${style} travel style and ${days}-day trip length`);
  if (cityCount > 1 && !hasFlightBookings && knownTransport === 0) {
    reasons.push(`${cityCount} cities — sync flights in Wallet to refine transport`);
  }
  if (partySize > 1) reasons.push(`Scaled for ${partySize} travelers`);
  reasons.push(`${bufferPct}% buffer for unexpected costs`);

  return {
    rangeMin,
    rangeMax,
    currency,
    drivers: {
      transport: Math.round((transportMin + transportMax) / 2),
      stay: Math.round((stayMin + stayMax) / 2),
      inDestination: Math.round((inDestMin + inDestMax) / 2),
    },
    breakdown: [
      { category: 'Transport', min: Math.round(transportMin * bufferFactor), max: Math.round(transportMax * bufferFactor) },
      { category: 'Lodging', min: Math.round(stayMin * bufferFactor), max: Math.round(stayMax * bufferFactor) },
      { category: 'Food & activities', min: Math.round(inDestMin * bufferFactor), max: Math.round(inDestMax * bufferFactor) },
    ],
    confidence,
    reasons,
    nights,
    partySize,
    style,
    bufferPct,
  };
}

export function shouldShowTripCostConfidence(trip: Trip, isArchived: boolean): boolean {
  if (isArchived || trip.isArchived) return false;
  const now = Date.now();
  if (trip.endDate.getTime() < now) return false;
  if (typeof trip.budget === 'number' && trip.budget > 0) return false;
  return Boolean(trip.destination || (trip.destinations && trip.destinations.length > 0));
}
